import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Ban from "~icons/ph/prohibit";
import Check from "~icons/ph/check";
import MessageCircle from "~icons/ph/chat-circle";
import Phone from "~icons/ph/phone";
import UserPlus from "~icons/ph/user-plus";
import Video from "~icons/ph/video-camera";
import X from "~icons/ph/x";
import type { ConnectionAction } from "../../shared/api";
import type { Person } from "../../shared/types";
import { useShell, type SafetyAction } from "../app/shell";
import { useSubmitting } from "../data/mutations";
import { peopleOptions } from "../data/queries";
import { useAccount } from "../data/session";
import { t } from "../i18n";
import { ActivityStatus, Avatar, Modal } from "../ui/components";
import { PersonRow, SafetyMenu, useConnectionAction } from "./person-row";

export function PersonDialog({
  person: viewed,
  code,
  search,
}: {
  person: Person;
  code?: string;
  search?: string;
}) {
  const shell = useShell();
  const account = useAccount();
  const me = account?.me ?? null,
    friends = account?.friends ?? [],
    requests = account?.requests ?? [],
    blockedPeople = account?.blocked ?? [];
  const submitting = useSubmitting();
  const requestAction = useConnectionAction();
  const accountId = me?.id ?? null;
  const results = useQuery({
    ...peopleOptions(accountId, search ?? ""),
    enabled: !!accountId && !!search,
  });
  const person =
    friends.find((friend) => friend.id === viewed.id) ??
    requests.find((request) => request.id === viewed.id) ??
    results.data?.find((result) => result.id === viewed.id) ??
    viewed;
  const personIsFriend = friends.some((friend) => friend.id === person.id);
  const personRequest = requests.find((request) => request.id === person.id);
  const personAction: ConnectionAction =
    personRequest || person.relation === "pending"
      ? (person.requester ?? personRequest?.requested_by) === me?.id
        ? "cancel-request"
        : "accept"
      : "request";
  return (
    <Modal title={person.name} className="profile-modal" onClose={shell.close}>
      <div className="profile-view user-profile">
        <SafetyMenu person={person} isFriend={personIsFriend} />
        <Avatar person={person} large dot={personIsFriend} />
        <h2>{person.name}</h2>
        <p className="profile-username" dir="ltr">
          @{person.username}
        </p>
        {personIsFriend && (
          <p className={`profile-status ${person.status}`}>
            <span>
              {person.status === "offline" && person.last_active_at != null && (
                <>{t("آخر ظهور")} </>
              )}
              <ActivityStatus person={person} />
            </span>
          </p>
        )}
        {person.bio && (
          <p className="profile-bio" dir="auto">
            {person.bio}
          </p>
        )}
        {personIsFriend ? (
          <div className="profile-contact">
            <button
              aria-label={t("اتصل بـ{name}", { name: person.name })}
              onClick={() => shell.callFriend(person)}
            >
              <Phone width={20} height={20} />
              {t("اتصل")}
            </button>
            <button
              aria-label={t("مكالمة فيديو مع {name}", {
                name: person.name,
              })}
              onClick={() => shell.callFriend(person, "video")}
            >
              <Video width={20} height={20} />
              {t("فيديو")}
            </button>
            <button
              aria-label={t("راسل {name}", { name: person.name })}
              onClick={() => shell.showChat(person)}
            >
              <MessageCircle width={20} height={20} />
              {t("راسل")}
            </button>
          </div>
        ) : blockedPeople.some((blocked) => blocked.id === person.id) ? (
          <>
            <button
              className="button secondary full"
              disabled={submitting}
              onClick={async () => {
                if (await requestAction(person, "unblock")) shell.close();
              }}
            >
              {t("إلغاء الحظر")}
            </button>
            <p className="profile-note">
              {t("إلغاء الحظر لا يعيد الصداقة أو طلبات الإضافة.")}
            </p>
          </>
        ) : (
          <>
            <button
              className="button full"
              disabled={submitting}
              onClick={() => {
                if (!me)
                  shell.open({
                    kind: "enter",
                    step: "join",
                    target: { person, code },
                  });
                else void requestAction(person, personAction, code);
              }}
            >
              {personAction === "cancel-request" ? (
                <X width={18} height={18} />
              ) : personAction === "accept" ? (
                <Check width={18} height={18} />
              ) : (
                <UserPlus width={18} height={18} />
              )}
              {t(
                personAction === "cancel-request"
                  ? "إلغاء الطلب"
                  : personAction === "accept"
                    ? "قبول"
                    : "إضافة",
              )}
            </button>
            <p className="profile-note">
              {t(
                personAction === "cancel-request"
                  ? "بانتظار قبول {name} لطلبك."
                  : personAction === "accept"
                    ? "أرسل {name} إليك طلب إضافة."
                    : "أضف {name} لتتمكنا من الاتصال والمراسلة.",
                { name: person.name },
              )}
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

export function RequestsDialog() {
  const { close } = useShell();
  const account = useAccount();
  const requests = account?.requests ?? [],
    meId = account?.me.id;
  const incoming = requests.filter((p) => p.requested_by !== meId),
    outgoing = requests.filter((p) => p.requested_by === meId);
  // The list closes once its last request is answered or withdrawn.
  useEffect(() => {
    if (!requests.length) close();
  }, [requests.length, close]);
  return (
    <Modal title={t("الطلبات")} className="list-modal" onClose={close}>
      <h2>{t("الطلبات")}</h2>
      {incoming.map((p) => (
        <PersonRow key={p.id} person={p} kind="incoming" />
      ))}
      {outgoing.length > 0 && (
        <>
          <h3 className="subheading">{t("الطلبات المرسلة")}</h3>
          {outgoing.map((p) => (
            <PersonRow key={p.id} person={p} kind="outgoing" />
          ))}
        </>
      )}
    </Modal>
  );
}

export function SafetyDialog({
  person,
  action,
}: {
  person: Person;
  action: SafetyAction;
}) {
  const shell = useShell();
  const submitting = useSubmitting();
  const requestAction = useConnectionAction();
  return (
    <Modal
      title={t(action === "block" ? "حظر الحساب" : "إزالة الصديق")}
      onClose={shell.close}
    >
      <h2>
        {t(action === "block" ? "حظر {name}؟" : "إزالة {name} من الأصدقاء؟", {
          name: person.name,
        })}
      </h2>
      <p className="modal-description">
        {t(
          action === "block"
            ? "سيُزال من الأصدقاء ولن يتمكن من مراسلتك أو الاتصال بك. يمكنك إلغاء الحظر من الإعدادات."
            : "ستتوقف الرسائل والمكالمات بينكما. ستحتاجان إلى طلب إضافة جديد للتواصل مجددًا.",
        )}
      </p>
      <div className="confirm-actions">
        <button
          className="button secondary"
          disabled={submitting}
          onClick={shell.close}
        >
          {t("رجوع")}
        </button>
        <button
          className="button danger-button"
          disabled={submitting}
          onClick={async () => {
            if (await requestAction(person, action)) shell.close();
          }}
        >
          {submitting ? (
            <span className="action-skeleton" aria-hidden="true" />
          ) : (
            <Ban width={16} height={16} />
          )}
          {t(action === "block" ? "حظر الحساب" : "إزالة الصديق")}
        </button>
      </div>
    </Modal>
  );
}
